"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { Sparkles, ShieldCheck, Building2, Search, Layers, CircleCheck as CheckCircle2, TrendingUp, ChevronDown, ArrowUpRight, Globe } from 'lucide-react';
interface CaseStudy {
  id: string;
  title: string;
  category: string;
  clientIndustry: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  metricValue: string;
  metricLabel: string;
  metricSubtext: string;
  challenge: string;
  strategy: string;
  implementation: string;
  results: string;
  impact: string;
}
const caseStudyLinks: Record<string, string> = {
  "digital-publishing-local-to-global": "/StartingLocalToGoGlobalCaseStudyPage",
  "fashion-retailer-organic-performance": "/FashionRetailerCaseStudyPage",
  "commercial-cleaning-marketing-roi": "/CommercialCleaningCaseStudyPage",
  "dermatology-practice-local-traffic": "/DermatologyPracticeCaseStudyPage"
};
const caseStudies: CaseStudy[] = [{
  id: "digital-publishing-local-to-global",
  title: "Starting Local to Go Global: A New Online Magazine's National SEO Breakthrough",
  category: "National SEO & Content Strategy",
  clientIndustry: "Digital Publishing & Online Media",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_digital_publishing.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_digital_publishing.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_digital_publishing.png-wvc-sizes",
  imageAlt: "Digital publishing magazine reaching national audience through SEO",
  metricValue: "+100%",
  metricLabel: "Organic Traffic Growth (Year 1)",
  metricSubtext: "Achieved by pivoting from local to national keyword authority",
  challenge: "The online magazine launched in 2016, founded by a news-media professional to give young professionals a place to read peer experiences and career advice. The platform had strong content but lacked a keyword strategy.",
  strategy: "Adopted a \"start local to go global\" approach — building topical and search authority through location-based keywords first, before pivoting to high-competition national terms.",
  implementation: "Targeted 17 local keywords to build trust and authority; tracked and adjusted rankings monthly; once local authority was established, pivoted the strategy toward broad, high-difficulty organic national keywords.",
  results: "11 of 17 local keywords reached Page 1 within 2 months (5 in the #1 spot); 13 of 17 within 4 months; achieved an average of 25 new readers per day and approximately 200 monthly clicks from search results, alongside page-1 keyword rankings and a +100% year-one traffic growth.",
  impact: "Transformed a fledgling local readership into a thriving national audience, establishing a durable organic search presence without relying on paid advertising."
}, {
  id: "fashion-retailer-organic-performance",
  title: "Transforming a Fashion Retailer's Organic Performance",
  category: "Technical SEO & Conversion Optimization",
  clientIndustry: "Fashion & Apparel (E-commerce)",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_fashion_ecommerce.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_fashion_ecommerce.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_fashion_ecommerce.png-wvc-sizes",
  imageAlt: "Fashion retailer e-commerce product display with increased organic traffic",
  metricValue: "+420%",
  metricLabel: "Increase in Organic Traffic",
  metricSubtext: "Over an 8-month direct consulting engagement",
  challenge: "A mid-sized online fashion retailer had a strong brand and quality products, but stagnant organic traffic, poor visibility for high-intent keywords, and underperforming landing pages were capping organic revenue.",
  strategy: "Executed a multi-phase SEO plan: deep keyword research and content gap analysis, on-page optimization, a full technical SEO overhaul, and conversion rate optimization.",
  implementation: "Rewrote product and category page meta titles, descriptions and headers; mapped keywords to specific buyer personas and customer journey stages; resolved crawl errors and improved site architecture; fixed Core Web Vitals and implemented structured data for rich results such as reviews and pricing; improved internal linking and simplified the checkout funnel.",
  results: "+420% increase in organic traffic, +280% increase in organic revenue, +65% boost in conversion rate, and Top 5 rankings secured for 25+ target keywords.",
  impact: "\"We knew SEO was important, but we had no idea it could drive this kind of growth. The work was strategic, measurable, and game-changing for our business.\" — Fatima Asrafy, Marketing Manager, Fashion Retailer (COO, Amerimar Enterprises, Inc.)"
}, {
  id: "commercial-cleaning-marketing-roi",
  title: "Improving Marketing ROI for a Commercial Cleaning Company",
  category: "Local SEO & Lead Generation",
  clientIndustry: "Commercial Cleaning Services",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_commercial_cleaning.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_commercial_cleaning.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_commercial_cleaning.png-wvc-sizes",
  imageAlt: "Commercial cleaning service team providing professional cleaning",
  metricValue: "3x",
  metricLabel: "Monthly Leads Growth",
  metricSubtext: "From 14 to 41 leads per month by month 4",
  challenge: "The company had been operating for nearly two decades before transitioning from small offices to corporate and residential clients. They had previously hired another SEO specialist with no real strategy, generating just 14 leads per month while spending nearly $5,000/month on marketing with poor results.",
  strategy: "Audited the existing site to define a clear roadmap, then rebuilt the technical and local SEO foundation to grow qualified bookings sustainably.",
  implementation: "Set up and optimized Google Business Profile and schema markup; conducted targeted keyword research; cleaned up site content to improve speed and UX; combined targeted SEO with monthly social media initiatives to build a multi-channel inbound lead pipeline; proactively reviewed performance monthly and adjusted underperforming keywords; expanded the keyword list to reach new customer segments.",
  results: "Tripled monthly leads from 14 to 41 by the start of the 4th month; website visits grew substantially; secured a #1 local organic ranking for core service keywords; generated repeat bookings from customers last serviced over 12 months prior.",
  impact: "Enabled the client to expand beyond small office spaces into commercial business centers and corporate offices, with continued growth today."
}, {
  id: "dermatology-practice-local-traffic",
  title: "Injecting Local Traffic to a Dermatology Practice",
  category: "Local SEO & Geolocation Targeting",
  clientIndustry: "Healthcare & Dermatology",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dermatology_clinic.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dermatology_clinic.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_dermatology_clinic.png-wvc-sizes",
  imageAlt: "Dermatology clinic consultation room with medical professional",
  metricValue: "143",
  metricLabel: "Keywords Ranking on Page 1",
  metricSubtext: "34 in positions 1-3, 33 in positions 4-10, 76 in positions 11-20",
  challenge: "Two highly skilled, award-winning practitioners invested heavily in a new clinic but relied solely on word-of-mouth referrals, resulting in slow growth and near-invisible search presence while competitors dominated local rankings.",
  strategy: "Focused on local search optimization and geolocation targeting to capture high-intent patients. 56% of relevant searches occur on mobile devices, and up to 50% of local searches lead directly to a phone inquiry or booking. Additionally, 93% of local searches surface Google's local pack.",
  implementation: "Conducted targeted keyword research for high booking-intent queries; set up and optimized Google Business Profile, Bing Local, Yelp, and 30+ niche healthcare directories; standardized NAP consistency across all channels; scaled structured local citations to reinforce geographic authority.",
  results: "Achieved #1 rankings on Yelp, Yellow Pages, Bing Local, and Apple Maps; #1 Google Local Pack position for 10 primary target keywords; 143 total keywords ranking on Page 1; 100+ additional organic search visits per month.",
  impact: "The practice expanded operations by hiring 2 additional doctors and began planning a satellite clinic to handle continued patient demand."
}];
export default function Casestudies() {
  const shouldReduceMotion = useReducedMotion();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="casestudiesshowcase" data-nav="light" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-50">
        <div className="absolute top-10 right-10 w-80 h-80 rounded-lg bg-secondary/40 blur-3xl" />
        <div className="absolute bottom-16 left-10 w-96 h-96 rounded-lg bg-muted blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16 lg:mb-24 pb-8 border-b border-border">
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="lg:col-span-4 flex flex-col items-start gap-4">
            <Badge variant="secondary" className="px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider bg-secondary text-[#F9004D] border border-secondary/80 rounded-lg">
              <Sparkles className="size-3.5 text-[#F9004D] mr-1.5" /> Verified Proven Results
            </Badge>

            <div className="p-4 rounded-lg bg-card border border-border text-xs sm:text-sm text-muted-foreground shadow-xs">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="size-4 shrink-0 text-[#F9004D] mt-0.5" />
                <p className="leading-relaxed">
                  <strong className="text-foreground font-medium">Confidentiality Note:</strong> Client names are anonymised where strict non-disclosure agreements apply. All search metrics and commercial numbers are 100% genuine.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="lg:col-span-8 flex flex-col justify-center">
            <h1 className="font-serif text-[clamp(1.8125rem,2.8vw,2.675rem)] font-bold tracking-tight leading-[1.15] text-foreground mb-5">
              Real Search Growth, Delivered Personally
            </h1>

            <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">
              I work directly with ambitious businesses to engineer sustainable organic search dominance. Every case study below represents real strategic execution, measurable business impact, and verified outcomes.
            </p>
          </motion.div>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 mb-20">
          {caseStudies.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i} className="h-full">
              <Card className="h-full border border-border bg-card shadow-xs hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-300 rounded-lg flex flex-col justify-between overflow-hidden group">
                <div>
                  <div className="relative h-56 sm:h-64 overflow-hidden bg-muted">
                    <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                    <div className="absolute top-4 left-4 right-4 flex items-center justify-between gap-2">
                      <Badge className="bg-secondary text-foreground font-semibold text-xs border border-border/80 shadow-xs rounded-lg px-2.5 py-1">
                        {item.category}
                      </Badge>
                      <div className="flex items-center gap-1.5 text-xs text-white/95 bg-black/50 backdrop-blur-md px-3 py-1 rounded-lg border border-white/15 font-sans">
                        <Building2 className="size-3.5 text-[#F9004D]" />
                        <span>{item.clientIndustry}</span>
                      </div>
                    </div>

                    <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-md p-4 rounded-lg border border-border/80 shadow-md flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#F9004D]">Primary Impact</p>
                        <p className="font-serif text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                          {item.metricValue}
                        </p>
                        <p className="text-xs text-foreground/80 font-medium">{item.metricLabel}</p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <span className="text-xs text-muted-foreground block max-w-[150px] leading-snug font-sans">
                          {item.metricSubtext}
                        </span>
                      </div>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <CardTitle className="font-serif text-xl sm:text-2xl font-semibold text-foreground leading-snug group-hover:text-primary transition-colors duration-250">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4 pt-2">
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#F9004D] mb-1.5">
                          <Search className="size-3.5" />
                          <span>The Challenge</span>
                        </div>
                        <p className="font-sans font-normal text-base text-muted-foreground leading-relaxed">
                          {item.challenge}
                        </p>
                      </div>

                      <Separator className="bg-border" />

                      <div>
                        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary mb-1.5">
                          <Layers className="size-3.5" />
                          <span>My Strategic Approach</span>
                        </div>
                        <p className="font-sans font-normal text-base text-muted-foreground leading-relaxed">
                          {item.strategy}
                        </p>
                      </div>
                    </div>

                    {expandedId === item.id && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: "auto"
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="space-y-4 pt-3 border-t border-border">
                        <div>
                          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary mb-1">
                            <CheckCircle2 className="size-3.5 text-primary" />
                            <span>Execution & Implementation</span>
                          </div>
                          <p className="font-sans font-normal text-base text-muted-foreground leading-relaxed">
                            {item.implementation}
                          </p>
                        </div>

                        <div>
                          <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary mb-1">
                            <TrendingUp className="size-3.5 text-primary" />
                            <span>Verified Search Results</span>
                          </div>
                          <p className="font-sans font-normal text-base text-muted-foreground leading-relaxed">
                            {item.results}
                          </p>
                        </div>

                        <div className="p-4 rounded-lg bg-secondary/50 border border-secondary">
                          <p className="text-xs font-bold uppercase tracking-wider text-[#F9004D] mb-1">
                            Commercial Business Impact
                          </p>
                          <p className="font-sans font-medium text-base text-foreground leading-relaxed">
                            {item.impact}
                          </p>
                        </div>
                      </motion.div>}
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-2">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-border">
                    <Button variant="ghost" size="sm" onClick={() => toggleExpand(item.id)} className="text-xs font-medium text-primary hover:text-primary hover:bg-secondary/60 p-2 sm:p-0 h-9 sm:h-auto gap-1.5 justify-center sm:justify-start transition-colors duration-250 rounded-md">
                      <span>{expandedId === item.id ? "Hide Breakdown" : "View Full Breakdown"}</span>
                      <ChevronDown className={`size-3.5 transition-transform duration-250 ${expandedId === item.id ? "rotate-180" : ""}`} />
                    </Button>

                    <Button asChild variant="outline" size="sm" className="text-xs font-semibold border-border bg-background hover:bg-[#F9004D] hover:border-[#F9004D] hover:text-white transition-all duration-250 h-9 px-4 rounded-lg">
                      <Link to={caseStudyLinks[item.id] || "/CaseStudiesPage"} className="inline-flex items-center justify-center gap-1.5">
                        <span>Read Case Study</span>
                        <ArrowUpRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="rounded-lg bg-card text-foreground border border-border p-8 sm:p-10 lg:p-12 shadow-md relative overflow-hidden flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 rounded-lg bg-secondary/50 blur-3xl pointer-events-none" />

          <div className="max-w-2xl relative z-10 text-center lg:text-left">
            <Badge className="bg-secondary text-[#F9004D] font-semibold text-xs mb-3 border border-secondary/80 rounded-lg">
              Direct Consultant Engagement
            </Badge>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-foreground mb-3">
              Ready for measurable, high-impact search growth?
            </h2>
            <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
              When you partner with me, you work directly with Yassine Essaadi — no junior account managers or generic agency templates. Let us discuss how to replicate these organic search results for your business.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 shrink-0 relative z-10 w-full lg:w-auto">
            <Button asChild size="lg" className="bg-[#F9004D] hover:bg-[#e00045] text-white font-semibold shadow-sm text-sm px-7 py-3 rounded-lg transition-all duration-250">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free Consultation</span>
                <ArrowUpRight className="size-4" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-foreground hover:text-background bg-background font-medium text-sm px-6 py-3 rounded-lg transition-all duration-250">
              <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                <Globe className="size-4" />
                <span>Explore All Case Studies</span>
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}